/**
 * A `NumericSearchWidgets` renders all the `NumericSearchField` according to the
 * `field_schema` objects returned in the `fields` field from a `/api/v1/search` endpoint.
 * Notice that all `fields_schema` objects which are numeric search are grouped
 * together into one sidebar section
 */
import React from 'react';
import immutable from 'immutable';

import NumericSearchField from './NumericSearchField';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import ConstValues from 'utils/const';

class NumericSearchWidgets extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isInitial: true,
      // NumericSearchField.values are off by 20% from min and max by default
      // to make the user realize that this is a slider
      valuesOffsetPercentage: 0.2,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.searchFields.count() > 0 && prevState.isInitial) {
      this.setState({
        isInitial: false,
      });
    }
  }

  /**
   * Convert `value`, which is a number of seconds, to a number of minutes
   * @param  {integer} value Number of seconds
   * @return {integer}       Number of minutes
   */
  convertSecondsToMinutes(value) {
    return value / 60;
  }

  /**
   * Convert `value`, which is a number of minutes, to a number of seconds
   * @param  {integer} value Number of minutes
   * @return {integer}       Number of seconds
   */
  convertMinutesToSeconds(value) {
    return Math.round(value * 60);
  }

  /**
   * Convert `min` and `max` in an object `values` from seconds to minutes
   * @param  {object} values An object with a `min` key and a `max` key
   * @return {object}        A new object with updated `min` and `max` values
   */
  convertValuesFromSecondsToMinutes(values) {
    return {
      min: this.convertSecondsToMinutes(values.min),
      max: this.convertSecondsToMinutes(values.max),
    };
  }

  /**
   * Convert `min` and `max` in an object `values` from minutes to seconds
   * @param  {object} values An object with a `min` key and a `max` key
   * @return {object}        A new object with updated `min` and `max` values
   */
  convertValuesFromMinutesToSeconds(values) {
    return {
      min: this.convertMinutesToSeconds(values.min),
      max: this.convertMinutesToSeconds(values.max),
    };
  }

  /**
   * Convert `values` from minutes to seconds only if it's necessary to do so
   * @param  {object} values A {min: %(min)d, max: %(max)d} object
   * @return {object}        A {min: %(min)d, max: %(max)d} object whose values are
   *                           converted from seconds to minutes if necessary
   */
  convertValuesFromMinutesToSecondsIfNecessary(sf, values) {
    if (ConstValues.numericSearchFieldNamesNeedSecondsToMinutesConversion.includes(sf.name)) {
      return this.convertValuesFromMinutesToSeconds(values);
    } else {
      return values;
    }
  }

  /**
   * Return a {min: %(min)f, max: %(max)f} object from a given `state`, `sfValue` and `sf`
   * @param  {immutable.Map}  state             The current state of the search field, not necessarily a true
   *                                            reflection about what's returned from the server
   * @param  {object}  sfValue           A {min, max} object retrieved from `sf`
   * @param  {object}  sf                A {value, min, max} search field object
   * @param  {Boolean} convertedToMinutes Should the min, max values be converted into minutes
   * @return {object}                    A {min, max} object
   */
  renderedValues(state, sfValue, sf, convertedToMinutes = false) {
    let values = null;
    if (state) {
      values = state.get('values');
    } else if (sfValue) {
      values = {
        min: sfValue.gte,
        max: sfValue.lte,
      };
    } else {
      values = {
        min: sf.min,
        max: sf.max,
      };
    }
    if (convertedToMinutes) {
      values = this.convertValuesFromSecondsToMinutes(values);
    }
    return values;
  }

  /**
   * Return the {min: %(min)d, max: %(max)d} values for searchField `sf`
   * @param  {records.Field} sf Field schema
   * @return {object}    A {min: %(min)d, max: %(max)d} object with values for `InputRange`
   */
  getNumericSearchFieldValues(sf) {
    const sfValue = sf.value;
    const state = this.props.searchFieldsState.get(sf.name);
    const shouldConvertValuesToMinutes =
      ConstValues.numericSearchFieldNamesNeedSecondsToMinutesConversion.includes(sf.name);
    let values = this.renderedValues(state, sfValue, sf, shouldConvertValuesToMinutes);
    if (this.state.isInitial) {
      // The initial min / max offsets of a numeric search field should be 20% and 80% so that the user
      // can realize that this widget is a slider
      const offset =
        this.state.valuesOffsetPercentage * (this.getNumericSearchFieldMaxValue(sf) - values.min);
      values = {
        min: values.min + offset,
        max: values.max - offset,
      };
    }

    return values;
  }

  /**
   * Return the `minValue` that's accepted by the <InputRange /> component
   * @param  {SearchField} sf Field schema
   * @return {integer}    A `InputRange.minValue` that's acceptable
   */
  getNumericSearchFieldMinValue(sf) {
    if (ConstValues.numericSearchFieldNamesNeedSecondsToMinutesConversion.includes(sf.name)) {
      return this.convertSecondsToMinutes(sf.min);
    } else {
      return sf.min;
    }
  }

  /**
   * Return the `maxValue` that's accepted by the <InputRange /> component
   * @param  {SearchField} sf Field schema
   * @return {integer}    A `InputRange.maxValue` that's acceptable
   */
  getNumericSearchFieldMaxValue(sf) {
    if (ConstValues.numericSearchFieldNamesNeedSecondsToMinutesConversion.includes(sf.name)) {
      return this.convertSecondsToMinutes(sf.max);
    } else {
      return sf.max;
    }
  }

  /**
   * Callback function that's triggered when the user slides the beginning or the end sliders
   * in a `NumericSearchField` component. In this function, we call
   * `SearchFieldsActions.updateNumericSearchField` to update the numeric search fields so that
   * the search expression used in /api/v1/search is correct and updated with the latest changes,
   * and `SearchFieldsActions.updateSearchFieldsState` in order to update the states of the numeric
   * search fields so that search widgets are rendered correctly
   * @param {records.Fields} searchField A numeric searchField object whose values should
   *                                     be updated in `SearchFieldsStore`
   * @param  {object} values A javascript object with {min: %(min)d, max: %(max)d} that specifies
   *                          the `min` and `max` of a range
   */
  onInputRangeChange(searchField, values) {
    const convertedValues = this.convertValuesFromMinutesToSecondsIfNecessary(searchField, values);
    SearchFieldsActions.updateSearchFieldsState({
      state: immutable.Map({
        name: searchField.name,
        type: searchField.type,
        values: convertedValues,
      }),
    });
    SearchFieldsActions.updateNumericSearchField(searchField, convertedValues);
  }

  /**
   * Return the `toggled` state value for a `searchField`
   * @param  {immutable/Record} searchField A SearchField record
   * @return {Boolean}             true if the searchField is toggled and false if not
   */
  getNumericSearchFieldToggled(searchField) {
    if (this.props.searchFieldsToggled) {
      const toggled = this.props.searchFieldsToggled.get(searchField.name);
      return toggled;
    }
    return false;
  }

  /**
   * Render a numeric search field for the provided search field.
   * @param  {searchField} The search field to render.
   * @return {JSX} A numeric search widget.
   */
  renderComponent(sf) {
    return (
      <NumericSearchField
        key={sf.name}
        searchField={sf}
        toggled={this.getNumericSearchFieldToggled(sf)}
        minValue={this.getNumericSearchFieldMinValue(sf)}
        maxValue={this.getNumericSearchFieldMaxValue(sf)}
        values={this.getNumericSearchFieldValues(sf)}
        updateLastToggledOnSearchWidget={this.props.updateLastToggledOnSearchWidget}
        shouldFocus={
          this.props.shouldRenderToggled &&
          this.getNumericSearchFieldToggled(sf) &&
          sf.name === this.props.lastToggledOnSearchWidget
        }
        shouldSortByToggled={this.props.shouldSortByToggled}
        onInputRangeChange={this.onInputRangeChange.bind(this)}
      />
    );
  }

  /**
   * Returns a boolean that indicates whether the Search Field should not be rendered.
   * @return {boolean} When true indicates that the Search Field should not be rendered.
   */
  shouldNotRenderComponent(sf) {
    const shouldRenderToggledAndNumericToggled =
      this.props.shouldRenderToggled && this.getNumericSearchFieldToggled(sf);
    const shouldRenderNotToggledAndNumericNotToggled =
      this.props.shouldRenderNotToggled && !this.getNumericSearchFieldToggled(sf);
    return !(shouldRenderToggledAndNumericToggled || shouldRenderNotToggledAndNumericNotToggled);
  }

  render() {
    const searchFields = this.props.searchFields.sort(sf => sf.order);
    const filteredSearchFields = searchFields.filterNot(this.shouldNotRenderComponent, this);
    return (
      <div className="search-widget">{filteredSearchFields.map(this.renderComponent, this)}</div>
    );
  }
}

export default NumericSearchWidgets;
